import {
  ContentList,
  GroupItemTitle,
  GroupTitleLearningPath,
  LearningPathTopicContainer,
  PlaylistTopicContainer,
  ProgressContainer,
  RowContent,
  SkillCheckContentList,
  SkillCheckTopicContainer,
  TopicTitle,
  UserRow,
  UsersRowContainer,
} from './styles';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { IPlaylistContentItem, IPlaylistUserDetails } from 'contexts/playlists/types';

import Avatar from './Avatar';
import ButtonIcon from 'components/buttonIcon';
import { Cell } from 'components/Table';
import Checkbox from 'components/checkbox';
import ContentProgressBar from './ContentProgressBar';
import ContentTypeTag, {
  hasSkillCheckContent,
} from 'pages/Playlists/components/AddContent/ContentTypeTag';
import DeadlineColumn from './DeadlineColumn';
import DonutProgressBar from 'components/donutProgressBar';
import Icon from 'components/icons';
import Notification from 'components/notification';
import { SessionContext } from 'contexts/session';
import { Spinner } from 'components/loadings';
import Tooltip from 'components/tooltip';
import { formatDistanceToNow } from 'date-fns';
import { getEligibleCollectionLabs } from 'utils/collectionLabs';
import { getPlaylistUserDetails } from 'services/playlists';
import { isMobile } from 'react-device-detect';
import styled from 'styled-components';
import {
  ACCOUNT_PLAYLIST_TOOLTIPS,
  COLLECTION_LABS_TITLE,
  REMOVE_USER_FROM_PLAYLIST_LABEL,
  TEAM_INHERITED_REMOVE_TOOLTIP,
} from 'constants/playlists';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  IContentProps,
  IPlaylistTopicProps,
  IUserRowParams,
  RemoveActionConfig,
  RemoveActionOptions,
  UsersTableContentItem,
} from './types';

const getAccountRemoveTooltip = (isAccountOwner: boolean) =>
  isAccountOwner ? ACCOUNT_PLAYLIST_TOOLTIPS.removeUserAO : ACCOUNT_PLAYLIST_TOOLTIPS.removeUserTM;

const getRemoveActionConfig = (
  item: import('contexts/playlists/types').IPlaylistUserListItemResponse,
  opts: RemoveActionOptions
): RemoveActionConfig => {
  const accountTooltip = getAccountRemoveTooltip(opts.isAccountOwner);

  if (typeof item.can_unassign === 'boolean') {
    if (item.can_unassign) {
      return { disabled: false, tooltip: REMOVE_USER_FROM_PLAYLIST_LABEL };
    }

    if (opts.isAccountPlaylist || item.assignment_source === 'auto_account') {
      return { disabled: true, tooltip: accountTooltip };
    }

    return { disabled: true, tooltip: TEAM_INHERITED_REMOVE_TOOLTIP };
  }

  if (!opts.accountPlaylistFeatureEnabled) {
    return { disabled: false, tooltip: REMOVE_USER_FROM_PLAYLIST_LABEL };
  }

  if (opts.accountPlaylistFeatureEnabled && opts.disableManualAssignments) {
    return {
      disabled: true,
      tooltip: accountTooltip,
    };
  }

  return { disabled: false, tooltip: REMOVE_USER_FROM_PLAYLIST_LABEL };
};

const ContentRow = ({ content, embeddedContent, tagVariant = 'iconText' }: IContentProps) => {
  const hasSkillCheckLabs = hasSkillCheckContent(content);
  return (
    <UsersRowContainer embeddedContent={embeddedContent} className="w-100">
      <RowContent embeddedContent={embeddedContent} noBorder={hasSkillCheckLabs}>
        <div className="content-type-tag-container">
          <ContentTypeTag
            value={content.content_type}
            variant={tagVariant}
            is_lab_experience={content.is_lab_experience}
            is_trackable={content.is_trackable ?? content.has_trackable_content}
            has_skill_check_labs={hasSkillCheckLabs}
          />
        </div>
        <GroupItemTitle>{content.name}</GroupItemTitle>
        <ContentProgressBar item={content as IPlaylistContentItem} />
      </RowContent>
      {content?.content_type === 'course' && hasSkillCheckLabs && (
        <SkillCheckTopic data={content} />
      )}
    </UsersRowContainer>
  );
};

const ProgressCell = styled(Cell)`
  display: flex;
  gap: 10px;
`;

/**
 * Child-lab rows under a collection in the per-learner breakdown. Lists the
 * collection's eligible labs only, so the rows an admin reads match the
 * `x/y labs` ratio on the row above them and the CSV export.
 */
const CollectionLabRows: React.FC<{ content: UsersTableContentItem }> = ({ content }) => {
  const labs = getEligibleCollectionLabs(content.labs ?? []);

  if (!labs.length) return null;

  return (
    <LearningPathTopicContainer>
      <GroupTitleLearningPath>{COLLECTION_LABS_TITLE}</GroupTitleLearningPath>
      <ContentList>
        {labs.map((lab, labIndex) => (
          <ContentRow
            key={lab.uuid ?? labIndex}
            content={{
              ...lab,
              content_type: 'lab',
              is_lab_experience: true,
              is_trackable: lab.has_flags,
            }}
            embeddedContent
            tagVariant="icon"
          />
        ))}
      </ContentList>
    </LearningPathTopicContainer>
  );
};

const PlaylistTopic: React.FC<IPlaylistTopicProps> = ({ data }) => (
  <PlaylistTopicContainer>
    <TopicTitle>{data.name}</TopicTitle>
    <ContentList>
      {data.content.map((content, index) => (
        <Fragment key={index}>
          <ContentRow content={content} embeddedContent />
          {content.content_type === 'collection' && <CollectionLabRows content={content} />}
          {content.content_type === 'learning-path' &&
            content.content.map((learningPathTopic, lpIndex) => (
              <LearningPathTopicContainer key={lpIndex}>
                <GroupTitleLearningPath>{learningPathTopic.name}</GroupTitleLearningPath>
                <ContentList>
                  {learningPathTopic.content.map((lpContent, cIndex) => (
                    <ContentRow
                      key={cIndex}
                      content={lpContent}
                      embeddedContent
                      tagVariant="icon"
                    />
                  ))}
                </ContentList>
              </LearningPathTopicContainer>
            ))}
        </Fragment>
      ))}
    </ContentList>
  </PlaylistTopicContainer>
);

type SkillCheckTopicProps = {
  data: {
    skill_check_labs?: import('./types').UsersTableContentItem[];
  };
};

const SkillCheckTopic: React.FC<SkillCheckTopicProps> = ({ data }) => (
  <SkillCheckContentList>
    {data.skill_check_labs?.map((skillCheckTopic, index) => (
      <SkillCheckTopicContainer key={skillCheckTopic?.id ?? skillCheckTopic?.uuid ?? index}>
        <ContentRow
          content={{
            ...(skillCheckTopic as import('./types').UsersTableContentItem),
            content_type: 'skill-check',
          }}
          embeddedContent
        />
      </SkillCheckTopicContainer>
    ))}
  </SkillCheckContentList>
);

const UsersRow: React.FC<IUserRowParams> = ({
  item,
  playlist,
  isAccountPlaylist,
  removeUser,
  isSonar,
  disableManualAssignments,
  selectionEnabled,
  isSelected = false,
  onToggleUserSelection,
  onEditDeadline,
}) => {
  const { account, isAccountOwner } = useContext(SessionContext);
  const { dashboardAccountPlaylistAssignment, playlistDeadlinesMvp } = useFlags() || {};
  const accountPlaylistFeatureEnabled = Boolean(dashboardAccountPlaylistAssignment);
  const [showDetails, setShowDetails] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [userDetails, setUserDetails] = useState<IPlaylistUserDetails | null>(null);
  const showDeadlineColumn = Boolean(playlistDeadlinesMvp && !isSonar);
  const showSelectionColumn = Boolean(selectionEnabled && showDeadlineColumn && !isMobile);
  const userName = `${item.user.first_name || ''} ${item.user.last_name || ''}`.trim();

  const columnsTableConfig = useMemo(
    () =>
      isMobile
        ? 'auto 100px'
        : showSelectionColumn
        ? '40px 6fr 3fr 3fr 3fr 3fr 3fr'
        : showDeadlineColumn
        ? '6fr 3fr 3fr 3fr 3fr 3fr'
        : '6fr 3fr 3fr 3fr 3fr',
    [showDeadlineColumn, showSelectionColumn]
  );

  const removeAction = useMemo(
    () =>
      getRemoveActionConfig(item, {
        disableManualAssignments,
        isAccountOwner,
        accountPlaylistFeatureEnabled,
        isAccountPlaylist,
      }),
    [
      item,
      disableManualAssignments,
      isAccountOwner,
      accountPlaylistFeatureEnabled,
      isAccountPlaylist,
    ]
  );

  const onRemoveUser = () => {
    if (removeAction.disabled) return;
    removeUser(item);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setUserDetails(null);
  };

  const fetchDetails = async () => {
    setShowDetails(true);
    setFetching(true);
    try {
      const response = await getPlaylistUserDetails({
        business_account_id: account.id,
        playlist_id: playlist.id,
        user_id: item.user.id,
      });
      if (response.status === 200) {
        setUserDetails(response.data);
      }
    } catch {
      Notification({ text: 'There has been a problem', type: 'error' });
      closeDetails();
    } finally {
      setFetching(false);
    }
  };

  const toggleDetails = async () => {
    if (showDetails) {
      closeDetails();
      return;
    }
    await fetchDetails();
  };

  useEffect(() => {
    if (!isSonar) return;
    fetchDetails();
  }, [isSonar]);

  return (
    <UsersRowContainer>
      <UserRow columns={columnsTableConfig}>
        {showSelectionColumn && (
          <Cell>
            <Checkbox
              aria-label={`Select ${userName || item.user.email || 'user'}`}
              aria-checked={isSelected}
              checked={isSelected}
              controlled
              onChange={() => onToggleUserSelection?.(item.user.id)}
            />
          </Cell>
        )}
        <Cell>
          <Avatar
            user={item.user}
            teamName={item.assignment_source === 'auto_team' ? item.user.team?.name : undefined}
          />
        </Cell>
        {!isMobile && (
          <>
            <ProgressCell>
              <DonutProgressBar progress={item.progress} />
              <div>
                <div>
                  {item.trackable_items_finished}/{item.trackable_items_total}
                </div>
                <div>{Math.round(item.progress * 100)}% complete</div>
              </div>
            </ProgressCell>
            <Cell>
              {item.items_started}/{item.items_total}
            </Cell>
            <Cell>
              {item.last_activity_date &&
                formatDistanceToNow(new Date(item.last_activity_date), { addSuffix: true })}
            </Cell>
            {showDeadlineColumn && (
              <Cell>
                <DeadlineColumn
                  deadline={item.deadline}
                  finishedDate={item.finished_date}
                  userId={item.user.id}
                  progress={item.progress}
                  onEdit={(userId) => onEditDeadline?.(userId, item.deadline)}
                />
              </Cell>
            )}
          </>
        )}
        <Cell style={{ justifyContent: 'flex-end', gap: '10px' }}>
          <Tooltip
            placement="bottomRight"
            overlayInnerStyle={{ textAlign: 'center' }}
            title={
              removeAction.tooltip ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: removeAction.tooltip,
                  }}
                />
              ) : null
            }
          >
            <span style={{ display: 'inline-flex' }}>
              <ButtonIcon
                $secondary
                size="small"
                icon="Trash"
                disabled={removeAction.disabled}
                onClick={onRemoveUser}
              />
            </span>
          </Tooltip>
          <Tooltip placement="bottomRight" title="Show progress details">
            <div style={{ cursor: 'pointer', padding: '8px' }}>
              <Icon
                size="small"
                color="#A793F6"
                icon={showDetails ? 'ChevronUp' : 'ChevronDown'}
                onClick={toggleDetails}
              />
            </div>
          </Tooltip>
        </Cell>
      </UserRow>
      <ProgressContainer visible={showDetails} transparent={isMobile} topSelect={false} top={false}>
        {fetching ? (
          <Spinner active size="50" position="relative" top="0px" />
        ) : (
          userDetails?.content.map((playlistTopic, index) => (
            <PlaylistTopic key={index} data={playlistTopic} />
          ))
        )}
      </ProgressContainer>
    </UsersRowContainer>
  );
};

export default UsersRow;
